import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { GitCompare, Play, Download, CheckCircle2, Database, Loader2, RefreshCw, AlertTriangle, History, Pencil, Unlink, Sparkles, X, Save, Trash2, Wand2, FolderOpen, ArrowUpDown, ArrowDown, ArrowUp, Search, FileText, Printer, StopCircle, Eye } from 'lucide-react';

const METHOD_COLORS = { exact: '#10b981', contains: '#3b82f6', llm: '#a855f7', manual: '#f59e0b' };
const METHOD_LABELS = { exact: 'Exact', contains: 'Contains', llm: 'AI Semantic', manual: 'Manual' };

const TableColumnPicker = ({ label, tables, table, column, column2, columns, onTable, onColumn, onColumn2, prefix }) => (
  <div className="space-y-3">
    <p className="text-sm font-semibold">{label}</p>
    <Select value={table} onValueChange={onTable}>
      <SelectTrigger data-testid={`${prefix}-table-select`}>
        <SelectValue placeholder="Select table" />
      </SelectTrigger>
      <SelectContent>
        {tables.map((t) => (
          <SelectItem key={t.name} value={t.name}>{t.name} ({t.row_count} rows)</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Select value={column} onValueChange={onColumn} disabled={!table}>
      <SelectTrigger data-testid={`${prefix}-column-select`}>
        <SelectValue placeholder="Select column to match" />
      </SelectTrigger>
      <SelectContent>
        {columns.map((c) => (
          <SelectItem key={c.name} value={c.name}>
            {c.name} ({c.data_type}){c.is_primary_key ? ' — PK' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Select value={column2 || '__none__'} onValueChange={(v) => onColumn2(v === '__none__' ? '' : v)} disabled={!table}>
      <SelectTrigger data-testid={`${prefix}-column2-select`}>
        <SelectValue placeholder="Additional column (optional)" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">No additional column</SelectItem>
        {columns.map((c) => (
          <SelectItem key={c.name} value={c.name}>
            + {c.name} ({c.data_type}){c.is_primary_key ? ' — PK' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const MatchCharts = ({ stats }) => {
  const donutData = [
    { name: 'Matched', value: stats.matched, color: '#10b981' },
    { name: 'Unmatched', value: stats.unmatched, color: '#ef4444' },
  ];
  const barData = Object.entries(stats.by_method).filter(([k]) => METHOD_LABELS[k]).map(([k, v]) => ({
    name: METHOD_LABELS[k], count: v, fill: METHOD_COLORS[k],
  }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card data-testid="match-donut-chart">
        <CardHeader className="pb-2"><CardTitle className="text-base">Matched vs Unmatched</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={donutData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {donutData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <p className="text-center text-sm text-muted-foreground -mt-2">
            {stats.matched} of {stats.total_source} source rows matched
            {stats.total_source > 0 && ` (${Math.round((stats.matched / stats.total_source) * 100)}%)`}
          </p>
        </CardContent>
      </Card>
      <Card data-testid="match-method-chart">
        <CardHeader className="pb-2"><CardTitle className="text-base">Matches by Method</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData}>
              <XAxis dataKey="name" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

const MatchLog = ({ logs }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [logs]);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Matching Log</CardTitle></CardHeader>
      <CardContent>
        <div ref={ref} data-testid="match-log" className="h-56 overflow-y-auto rounded-md bg-zinc-950 text-zinc-100 font-mono text-xs p-3 space-y-1">
          {(logs || []).map((l, i) => (
            <div key={`${l.ts}-${i}`} className={l.level === 'error' ? 'text-red-400' : l.level === 'warning' ? 'text-amber-400' : 'text-zinc-100'}>
              <span className="text-zinc-500">{l.ts?.slice(11, 19)}</span> {l.message}
            </div>
          ))}
          {!logs?.length && <p className="text-zinc-500">Waiting for logs...</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export default function DataMatching() {
  const { api } = useAuth();
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tablesError, setTablesError] = useState(null);
  const [src, setSrc] = useState({ table: '', column: '', column2: '' });
  const [tgt, setTgt] = useState({ table: '', column: '', column2: '' });
  const [combineOp, setCombineOp] = useState('and');
  const [srcCols, setSrcCols] = useState([]);
  const [tgtCols, setTgtCols] = useState([]);
  const [methods, setMethods] = useState({ exact: true, contains: true, llm: true });
  const [containsDir, setContainsDir] = useState('both');
  const [promptText, setPromptText] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [minConf, setMinConf] = useState(60);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [promptName, setPromptName] = useState('');
  const [promptBusy, setPromptBusy] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [showSaveConfig, setShowSaveConfig] = useState(false);
  const [configName, setConfigName] = useState('');
  const [configBusy, setConfigBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genDescription, setGenDescription] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [resultFilter, setResultFilter] = useState('all');
  const [accMode, setAccMode] = useState('any');
  const [accMin, setAccMin] = useState(60);
  const [accMax, setAccMax] = useState(90);
  const [confSort, setConfSort] = useState('none');
  const [results, setResults] = useState([]);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsOffset, setResultsOffset] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunLimit, setRerunLimit] = useState(200);
  const [rerunAll, setRerunAll] = useState(false);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [viewResult, setViewResult] = useState(null);
  const PAGE_SIZE = 100;
  const [editingResult, setEditingResult] = useState(null);
  const [targetQuery, setTargetQuery] = useState('');
  const [targetOptions, setTargetOptions] = useState([]);
  const [editBusy, setEditBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [wbColumn, setWbColumn] = useState('');
  const [wbPreview, setWbPreview] = useState(null);
  const [wbBusy, setWbBusy] = useState(false);
  const [wbTest, setWbTest] = useState(null);
  const [wbTestBusy, setWbTestBusy] = useState(false);
  const [wbCols, setWbCols] = useState([]);
  const pollRef = useRef(null);

  const loadTables = useCallback(async () => {
    setTablesLoading(true);
    setTablesError(null);
    try {
      const res = await api().get('/matching/tables');
      setTables(res.data.tables);
    } catch (e) {
      setTablesError(e.response?.data?.detail || 'Could not reach Azure SQL. The database may be resuming — retry in a moment.');
    } finally {
      setTablesLoading(false);
    }
  }, [api]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await api().get('/matching/jobs');
      setJobs(res.data.jobs);
    } catch (e) { /* non-blocking */ }
  }, [api]);

  useEffect(() => { loadTables(); loadJobs(); }, [loadTables, loadJobs]);

  useEffect(() => {
    api().get('/matching/prompt').then((res) => {
      setDefaultPrompt(res.data.prompt);
      setPromptText(res.data.prompt);
    }).catch(() => {});
    api().get('/matching/prompts').then((res) => setSavedPrompts(res.data.prompts)).catch(() => {});
    api().get('/matching/configs').then((res) => setSavedConfigs(res.data.configs)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePrompt = async () => {
    setGenBusy(true);
    try {
      const res = await api().post('/matching/generate-prompt', { description: genDescription.trim() });
      setPromptText(res.data.prompt);
      setSelectedPromptId('');
      setGenOpen(false);
      setGenDescription('');
      toast.success('Prompt generated — review it, tweak if needed, and save it for reuse');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Prompt generation failed');
    } finally {
      setGenBusy(false);
    }
  };

  const loadConfig = (id) => {
    const c = savedConfigs.find((x) => x.id === id);
    if (!c) return;
    setSelectedConfigId(id);
    const cfg = c.config;
    setSrc({ table: cfg.source_table, column: cfg.source_column, column2: cfg.source_column2 || '' });
    setTgt({ table: cfg.target_table, column: cfg.target_column, column2: cfg.target_column2 || '' });
    setCombineOp(cfg.combine_op || 'and');
    setSrcCols([]); setTgtCols([]);
    loadColumns(cfg.source_table, setSrcCols);
    loadColumns(cfg.target_table, setTgtCols);
    setMethods({ exact: cfg.use_exact, contains: cfg.use_contains, llm: cfg.use_llm });
    setContainsDir(cfg.contains_direction || 'both');
    if (cfg.llm_prompt) setPromptText(cfg.llm_prompt);
    setMinConf(Math.round((cfg.llm_min_confidence ?? 0.6) * 100));
    toast.info(`Loaded configuration "${c.name}"`);
  };

  const saveConfig = async () => {
    setConfigBusy(true);
    try {
      const res = await api().post('/matching/configs', {
        name: configName.trim(),
        config: {
          source_table: src.table, source_column: src.column,
          target_table: tgt.table, target_column: tgt.column,
          source_column2: src.column2 || null, target_column2: tgt.column2 || null,
          combine_op: combineOp,
          use_exact: methods.exact, use_contains: methods.contains, use_llm: methods.llm,
          contains_direction: containsDir,
          llm_prompt: promptText || null, llm_min_confidence: minConf / 100,
        },
      });
      setSavedConfigs((p) => [res.data, ...p]);
      setSelectedConfigId(res.data.id);
      setShowSaveConfig(false);
      setConfigName('');
      toast.success(`Configuration "${res.data.name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setConfigBusy(false);
    }
  };

  const deleteConfig = async () => {
    const c = savedConfigs.find((x) => x.id === selectedConfigId);
    if (!c) return;
    setConfigBusy(true);
    try {
      await api().delete(`/matching/configs/${c.id}`);
      setSavedConfigs((list) => list.filter((x) => x.id !== c.id));
      setSelectedConfigId('');
      toast.success(`Configuration "${c.name}" deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setConfigBusy(false);
    }
  };

  const savePrompt = async () => {
    setPromptBusy(true);
    try {
      const res = await api().post('/matching/prompts', { name: promptName.trim(), prompt: promptText });
      setSavedPrompts((p) => [res.data, ...p]);
      setSelectedPromptId(res.data.id);
      setShowSavePrompt(false);
      setPromptName('');
      toast.success(`Prompt "${res.data.name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setPromptBusy(false);
    }
  };

  const deletePrompt = async () => {
    const p = savedPrompts.find((x) => x.id === selectedPromptId);
    if (!p) return;
    setPromptBusy(true);
    try {
      await api().delete(`/matching/prompts/${p.id}`);
      setSavedPrompts((list) => list.filter((x) => x.id !== p.id));
      setSelectedPromptId('');
      toast.success(`Prompt "${p.name}" deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete prompt');
    } finally {
      setPromptBusy(false);
    }
  };

  useEffect(() => {
    if (!editingResult || !job) return;
    const t = setTimeout(async () => {
      try {
        const res = await api().get(`/matching/jobs/${job.id}/targets`, { params: { q: targetQuery, limit: 15 } });
        setTargetOptions(res.data.targets);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [editingResult, targetQuery, job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadColumns = async (table, setter) => {
    try {
      const res = await api().get(`/matching/tables/${table}/columns`);
      setter(res.data.columns);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load columns');
    }
  };

  const fetchJob = useCallback(async (id) => {
    try {
      const res = await api().get(`/matching/jobs/${id}`);
      setJob(res.data);
      return res.data;
    } catch (e) { return null; }
  }, [api]);

  useEffect(() => {
    if (job && (job.status === 'pending' || job.status === 'running')) {
      pollRef.current = setInterval(async () => {
        const j = await fetchJob(job.id);
        if (j && j.status !== 'pending' && j.status !== 'running') {
          clearInterval(pollRef.current);
          loadJobs();
          if (j.status === 'completed') toast.success('Matching completed — review and approve the results');
          if (j.status === 'failed') toast.error('Matching job failed — check the log');
        }
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.id, job?.status, fetchJob, loadJobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const startJob = async () => {
    setStarting(true);
    setWbPreview(null);
    setWbColumn('');
    setEditingResult(null);
    resetResultView();
    try {
      const res = await api().post('/matching/jobs', {
        source_table: src.table, source_column: src.column,
        target_table: tgt.table, target_column: tgt.column,
        source_column2: src.column2 || null, target_column2: tgt.column2 || null,
        combine_op: combineOp,
        use_exact: methods.exact, use_contains: methods.contains, use_llm: methods.llm,
        contains_direction: containsDir,
        llm_prompt: promptText || null,
        llm_min_confidence: minConf / 100,
      });
      toast.info('Matching started');
      await fetchJob(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start matching');
    } finally {
      setStarting(false);
    }
  };

  const approve = async () => {
    try {
      await api().post(`/matching/jobs/${job.id}/approve`);
      setEditingResult(null);
      toast.success('Results approved — you can now export CSV or update the source table');
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api().get(`/matching/jobs/${job.id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matching_${job.id.slice(0, 8)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV download failed');
    }
  };

  const fetchResults = useCallback(async (jobId, offset = 0) => {
    setResultsLoading(true);
    try {
      const res = await api().get(`/matching/jobs/${jobId}/results`, {
        params: {
          offset, limit: PAGE_SIZE, status: resultFilter,
          acc_mode: accMode, acc_min: accMin || 0, acc_max: accMax || 100, sort: confSort,
          q: searchQ,
        },
      });
      setResults(res.data.results);
      setResultsTotal(res.data.total);
      setResultsOffset(offset);
    } catch (e) { /* ignore */ } finally {
      setResultsLoading(false);
    }
  }, [api, resultFilter, accMode, accMin, accMax, confSort, searchQ]);

  useEffect(() => {
    if (job?.status !== 'completed') return;
    const t = setTimeout(() => fetchResults(job.id, 0), 300);
    return () => clearTimeout(t);
  }, [job?.id, job?.status, fetchResults]); // eslint-disable-line react-hooks/exhaustive-deps

  const rerunUnmatched = async () => {
    setRerunBusy(true);
    try {
      const res = await api().post(`/matching/jobs/${job.id}/rerun-unmatched`, {
        llm_prompt: promptText || null,
        llm_min_confidence: minConf / 100,
        limit: rerunLimit,
        process_all: rerunAll,
      });
      toast.info(`Rerunning AI matching on ${res.data.processing} of ${res.data.unmatched_total} unmatched rows`);
      setRerunOpen(false);
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rerun failed');
    } finally {
      setRerunBusy(false);
    }
  };

  const cancelRerun = async () => {
    try {
      await api().post(`/matching/jobs/${job.id}/cancel-rerun`);
      toast.info('Cancelling after the current batch group…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const openReport = async () => {
    try {
      const res = await api().get(`/matching/jobs/${job.id}/report`);
      setReportData(res.data);
      setReportOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build report');
    }
  };

  const printReport = () => {
    if (!reportData) return;
    const r = reportData;
    const rate = r.stats.total_source ? Math.round((r.stats.matched / r.stats.total_source) * 100) : 0;
    const maxDist = Math.max(1, ...r.confidence_distribution.map((d) => d.count));
    const bar = (n, max) => `<div style="background:#4f46e5;height:12px;width:${Math.round((n / max) * 300)}px;display:inline-block;border-radius:3px"></div>`;
    const html = `<!DOCTYPE html><html><head><title>Match Quality Report</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{font-size:22px}h2{font-size:15px;margin-top:24px}
      table{border-collapse:collapse;margin-top:8px}td,th{border:1px solid #ccc;padding:6px 12px;font-size:13px;text-align:left}
      .big{font-size:36px;font-weight:bold;color:#4f46e5}</style></head><body>
      <h1>Match Quality Report</h1>
      <p><b>${r.config.source_table}.${r.config.source_column}</b> → <b>${r.config.target_table}.${r.config.target_column}</b><br/>
      Job created: ${(r.created_at || '').slice(0, 19).replace('T', ' ')} &nbsp;|&nbsp; Report generated: ${(r.generated_at || '').slice(0, 19).replace('T', ' ')}<br/>
      Status: ${r.approved ? 'Approved' : 'Pending approval'}${r.writeback?.executed ? ` | Write-back: ${r.writeback.rows_updated} rows → ${r.writeback.table}.${r.writeback.column}` : ''}</p>
      <h2>Match rate</h2><div class="big">${rate}%</div>
      <p>${r.stats.matched} of ${r.stats.total_source} source rows matched (${r.stats.unmatched} unmatched, ${r.stats.total_target} target rows)</p>
      <h2>Method breakdown</h2><table><tr><th>Method</th><th>Matches</th></tr>
      ${Object.entries(r.stats.by_method).map(([k, v]) => `<tr><td>${METHOD_LABELS[k] || k}</td><td>${v}</td></tr>`).join('')}</table>
      <h2>Confidence distribution (matched rows)</h2><table><tr><th>Accuracy</th><th>Count</th><th></th></tr>
      ${r.confidence_distribution.map((d) => `<tr><td>${d.bucket}</td><td>${d.count}</td><td>${bar(d.count, maxDist)}</td></tr>`).join('')}</table>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const editMatch = async (resultId, body) => {
    setEditBusy(true);
    try {
      const res = await api().patch(`/matching/jobs/${job.id}/results/${resultId}`, body);
      setJob((prev) => ({ ...prev, stats: res.data.stats }));
      setResults((prev) => prev.map((r) => (r.id === resultId ? res.data.result : r)));
      setEditingResult(null);
      setTargetQuery('');
      toast.success(body.action === 'unmatch' ? 'Match removed' : 'Match updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Edit failed');
    } finally {
      setEditBusy(false);
    }
  };

  const testWriteback = async () => {
    setWbTestBusy(true);
    setWbTest(null);
    try {
      const res = await api().post(`/matching/jobs/${job.id}/writeback`, { update_column: wbColumn, test_one: true });
      setWbTest(res.data);
    } catch (e) {
      setWbTest({ success: false, error: e.response?.data?.detail || 'Test request failed' });
    } finally {
      setWbTestBusy(false);
    }
  };

  const downloadSqlScript = async () => {
    try {
      const res = await api().get(`/matching/jobs/${job.id}/writeback-script`, {
        responseType: 'blob', params: { update_column: wbColumn },
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `writeback_${job.config.source_table}_${job.id.slice(0, 8)}.sql`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('SQL update script downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Script download failed');
    }
  };


  const runWriteback = async (dryRun) => {
    setWbBusy(true);
    try {
      const res = await api().post(`/matching/jobs/${job.id}/writeback`, { update_column: wbColumn, dry_run: dryRun });
      if (dryRun) {
        setWbPreview(res.data);
      } else {
        toast.success(`Updated ${res.data.rows_updated} rows in ${res.data.table}.${res.data.column}`);
        setWbPreview(null);
        fetchJob(job.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Write-back failed');
    } finally {
      setWbBusy(false);
    }
  };

  const canRun = src.table && src.column && tgt.table && tgt.column &&
    (methods.exact || methods.contains || methods.llm) &&
    (Boolean(src.column2) === Boolean(tgt.column2)) &&
    !(job && (job.status === 'pending' || job.status === 'running'));

  const wbEligibleCols = wbCols.filter((c) => !c.is_primary_key);

  useEffect(() => {
    if (job?.status === 'completed' && job?.approved && job?.config?.source_table) {
      loadColumns(job.config.source_table, setWbCols);
    }
  }, [job?.id, job?.status, job?.approved]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetResultView = () => { setResultFilter('all'); setAccMode('any'); setAccMin(60); setAccMax(90); setConfSort('none'); setSearchQ(''); setResults([]); setResultsTotal(0); setResultsOffset(0); };
  const cycleSort = () => setConfSort((s) => (s === 'none' ? 'desc' : s === 'desc' ? 'asc' : 'none'));

  return (
    <div className="space-y-8" data-testid="data-matching-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <GitCompare className="w-8 h-8 text-primary" /> Data Matching
        </h1>
        <p className="text-muted-foreground mt-1">
          Match records between two Azure SQL tables using Exact → Contains → AI semantic matching.
        </p>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Database className="w-5 h-5" /> Configuration</CardTitle>
          <CardDescription>Pick the source and target table/column, choose the matching layers, then run.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {tablesError ? (
            <div className="flex items-center gap-3 p-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="azure-error-banner">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <span className="flex-1">{tablesError}</span>
              <Button size="sm" variant="outline" onClick={loadTables} data-testid="retry-azure-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Retry
              </Button>
            </div>
          ) : tablesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Connecting to Azure SQL and loading tables...
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 pb-4 border-b">
                <FolderOpen className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedConfigId} onValueChange={loadConfig}>
                  <SelectTrigger className="w-72" data-testid="saved-configs-select">
                    <SelectValue placeholder={savedConfigs.length ? 'Load a saved configuration...' : 'No saved configurations yet'} />
                  </SelectTrigger>
                  <SelectContent>
                    {savedConfigs.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} — {c.config.source_table}.{c.config.source_column} → {c.config.target_table}.{c.config.target_column}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedConfigId && (
                  <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" disabled={configBusy}
                    onClick={deleteConfig} data-testid="delete-config-btn" title="Delete selected configuration">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                {!showSaveConfig ? (
                  <Button variant="outline" size="sm" disabled={!(src.table && src.column && tgt.table && tgt.column)}
                    onClick={() => setShowSaveConfig(true)} data-testid="save-config-btn" title="Save the current selection as a reusable configuration">
                    <Save className="w-4 h-4 mr-1" /> Save configuration
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input className="w-56 h-9" placeholder="Configuration name" value={configName} autoFocus
                      onChange={(e) => setConfigName(e.target.value)} data-testid="config-name-input"
                      onKeyDown={(e) => { if (e.key === 'Enter' && configName.trim()) saveConfig(); }} />
                    <Button size="sm" disabled={!configName.trim() || configBusy} onClick={saveConfig} data-testid="confirm-save-config-btn">
                      {configBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowSaveConfig(false); setConfigName(''); }} data-testid="cancel-save-config-btn">
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <TableColumnPicker
                  label="Source (rows to match)" prefix="source" tables={tables}
                  table={src.table} column={src.column} column2={src.column2} columns={srcCols}
                  onTable={(t) => { setSrc({ table: t, column: '', column2: '' }); setSrcCols([]); loadColumns(t, setSrcCols); }}
                  onColumn={(c) => setSrc((s) => ({ ...s, column: c }))}
                  onColumn2={(c) => setSrc((s) => ({ ...s, column2: c }))}
                />
                <TableColumnPicker
                  label="Target (match against)" prefix="target" tables={tables}
                  table={tgt.table} column={tgt.column} column2={tgt.column2} columns={tgtCols}
                  onTable={(t) => { setTgt({ table: t, column: '', column2: '' }); setTgtCols([]); loadColumns(t, setTgtCols); }}
                  onColumn={(c) => setTgt((s) => ({ ...s, column: c }))}
                  onColumn2={(c) => setTgt((s) => ({ ...s, column2: c }))}
                />
              </div>
              {(src.column2 || tgt.column2) && (
                <div className="flex flex-wrap items-center gap-3 p-3 rounded-md border bg-muted/40" data-testid="combine-op-row">
                  {src.column2 && tgt.column2 ? (
                    <>
                      <Label className="text-sm font-semibold">Combine the two column pairs with:</Label>
                      <Select value={combineOp} onValueChange={setCombineOp}>
                        <SelectTrigger className="w-72 h-9" data-testid="combine-op-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">AND — both columns must match</SelectItem>
                          <SelectItem value="or">OR — either column may match</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground">
                        {src.column} ↔ {tgt.column} {combineOp.toUpperCase()} {src.column2} ↔ {tgt.column2}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-amber-700 flex items-center gap-2" data-testid="column2-warning">
                      <AlertTriangle className="w-4 h-4" /> Select the additional column on BOTH source and target (or set both to "No additional column").
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-8 pt-2 border-t">
                {[
                  ['exact', '1. Exact match'],
                  ['contains', '2. Contains match'],
                  ['llm', '3. AI semantic (RAG)'],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Switch id={`m-${key}`} checked={methods[key]} data-testid={`method-${key}-switch`}
                      onCheckedChange={(v) => setMethods((m) => ({ ...m, [key]: v }))} />
                    <Label htmlFor={`m-${key}`} className="text-sm">{label}</Label>
                    {key === 'contains' && methods.contains && (
                      <Select value={containsDir} onValueChange={setContainsDir}>
                        <SelectTrigger className="w-52 h-8 text-xs" data-testid="contains-direction-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="both">Either direction</SelectItem>
                          <SelectItem value="target_contains_source">Target contains source</SelectItem>
                          <SelectItem value="source_contains_target">Source contains target</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
                <Button onClick={startJob} disabled={!canRun || starting} className="ml-auto" data-testid="run-matching-btn">
                  {starting || (job && ['pending', 'running'].includes(job.status))
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Matching
                </Button>
              </div>
              {methods.llm && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" /> AI matching prompt (editable)
                    </Label>
                    {promptText !== defaultPrompt && (
                      <Button variant="ghost" size="sm" onClick={() => setPromptText(defaultPrompt)} data-testid="reset-prompt-btn">
                        Reset to default
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={selectedPromptId} onValueChange={(id) => {
                      setSelectedPromptId(id);
                      const p = savedPrompts.find((x) => x.id === id);
                      if (p) setPromptText(p.prompt);
                    }}>
                      <SelectTrigger className="w-64" data-testid="saved-prompts-select">
                        <SelectValue placeholder={savedPrompts.length ? 'Load a saved prompt...' : 'No saved prompts yet'} />
                      </SelectTrigger>
                      <SelectContent>
                        {savedPrompts.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedPromptId && (
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" disabled={promptBusy}
                        onClick={deletePrompt} data-testid="delete-prompt-btn" title="Delete selected saved prompt">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {!showSavePrompt ? (
                      <Button variant="outline" size="sm" onClick={() => setShowSavePrompt(true)} data-testid="save-prompt-btn">
                        <Save className="w-4 h-4 mr-1" /> Save prompt
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input className="w-52 h-9" placeholder="Prompt name (e.g. Loose matching)" value={promptName}
                          autoFocus onChange={(e) => setPromptName(e.target.value)} data-testid="prompt-name-input"
                          onKeyDown={(e) => { if (e.key === 'Enter' && promptName.trim()) savePrompt(); }} />
                        <Button size="sm" disabled={!promptName.trim() || promptBusy} onClick={savePrompt} data-testid="confirm-save-prompt-btn">
                          {promptBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setShowSavePrompt(false); setPromptName(''); }} data-testid="cancel-save-prompt-btn">
                          Cancel
                        </Button>
                      </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setGenOpen(!genOpen)} data-testid="generate-prompt-btn"
                      className="text-purple-700 border-purple-300 hover:bg-purple-50">
                      <Wand2 className="w-4 h-4 mr-1" /> Generate with AI
                    </Button>
                  </div>
                  {genOpen && (
                    <div className="border border-purple-300 bg-purple-50 rounded-md p-3 space-y-2" data-testid="prompt-generator-panel">
                      <Label className="text-sm">Describe what you want to match, and AI writes the prompt for you</Label>
                      <Textarea rows={2} value={genDescription} autoFocus data-testid="generate-description-input"
                        placeholder='e.g. "Match Arabic law titles against document file names — same law number and year means a match, even across languages"'
                        onChange={(e) => setGenDescription(e.target.value)} className="bg-white text-sm" />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={genDescription.trim().length < 3 || genBusy} onClick={generatePrompt} data-testid="confirm-generate-btn">
                          {genBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                          {genBusy ? 'Generating...' : 'Generate prompt'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setGenOpen(false)} data-testid="cancel-generate-btn">Cancel</Button>
                      </div>
                    </div>
                  )}
                  <Textarea value={promptText} onChange={(e) => setPromptText(e.target.value)} rows={4}
                    data-testid="llm-prompt-textarea" className="font-mono text-xs" />
                  <p className="text-xs text-muted-foreground">
                    Defines how the AI decides if two values match. Make it looser (e.g. "related topics count as matches")
                    to match more, or stricter to match less. The JSON reply format is enforced automatically.
                  </p>
                  <div className="pt-2 max-w-md space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Minimum AI confidence</Label>
                      <span className="text-sm font-semibold tabular-nums" data-testid="min-confidence-value">{minConf}%</span>
                    </div>
                    <Slider value={[minConf]} onValueChange={(v) => setMinConf(v[0])} min={0} max={100} step={5}
                      data-testid="min-confidence-slider" />
                    <p className="text-xs text-muted-foreground">
                      AI matches below this confidence are discarded. Lower it (e.g. 40%) to accept more matches; raise it for stricter results.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Running / results */}
      {job && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Badge data-testid="job-status-badge" variant={job.status === 'failed' ? 'destructive' : 'default'}
              className={job.status === 'completed' ? 'bg-emerald-600' : job.status === 'running' ? 'bg-blue-600' : ''}>
              {job.status.toUpperCase()}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {job.config.source_table}.{job.config.source_column} → {job.config.target_table}.{job.config.target_column}
            </span>
            {job.approved && <Badge className="bg-emerald-700" data-testid="approved-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>}
            {job.writeback?.executed && <Badge variant="outline" data-testid="writeback-badge">Write-back done ({job.writeback.rows_updated} rows)</Badge>}
            {job.status === 'completed' && (
              <Button variant="outline" size="sm" className="ml-auto" onClick={openReport} data-testid="quality-report-btn">
                <FileText className="w-4 h-4 mr-1" /> Quality Report
              </Button>
            )}
          </div>

          {job.status === 'running' && job.rerun_progress && (
            <Card className="border-blue-300" data-testid="rerun-progress-card">
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    AI rerun in progress — {job.rerun_progress.processed} / {job.rerun_progress.total} processed,{' '}
                    {job.rerun_progress.matched} newly matched
                  </p>
                  <Button variant="outline" size="sm" onClick={cancelRerun} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="cancel-rerun-running-btn">
                    <StopCircle className="w-4 h-4 mr-1" /> Cancel rerun
                  </Button>
                </div>
                <Progress value={job.rerun_progress.total ? (job.rerun_progress.processed / job.rerun_progress.total) * 100 : 0} data-testid="rerun-progress-bar" />
              </CardContent>
            </Card>
          )}

          {job.stats && <MatchCharts stats={job.stats} />}
          <MatchLog logs={job.logs} />

          {job.status === 'completed' && (
            <>
              {/* Results sample */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Match Results</CardTitle>
                  {!job.approved && (
                    <CardDescription>Review each match — use Edit to pick a different target or Unlink to remove a wrong match, then approve.</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex rounded-md border overflow-hidden">
                      {[
                        ['all', `All (${job.stats?.total_source || 0})`],
                        ['matched', `Matched (${job.stats?.matched || 0})`],
                        ['unmatched', `Unmatched (${job.stats?.unmatched || 0})`],
                      ].map(([key, label]) => (
                        <button key={key} onClick={() => setResultFilter(key)} data-testid={`results-filter-${key}`}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                            resultFilter === key
                              ? key === 'unmatched' ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground'
                              : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Select value={accMode} onValueChange={setAccMode}>
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid="accuracy-filter-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any accuracy</SelectItem>
                          <SelectItem value="gte">Accuracy ≥</SelectItem>
                          <SelectItem value="lt">Accuracy &lt;</SelectItem>
                          <SelectItem value="between">Accuracy between</SelectItem>
                        </SelectContent>
                      </Select>
                      {accMode !== 'any' && (
                        <>
                          <Input type="number" min={0} max={100} value={accMin} data-testid="accuracy-min-input"
                            onChange={(e) => setAccMin(Number(e.target.value))} className="w-16 h-8 text-xs" />
                          {accMode === 'between' && (
                            <>
                              <span className="text-xs text-muted-foreground">and</span>
                              <Input type="number" min={0} max={100} value={accMax} data-testid="accuracy-max-input"
                                onChange={(e) => setAccMax(Number(e.target.value))} className="w-16 h-8 text-xs" />
                            </>
                          )}
                          <span className="text-xs text-muted-foreground">%</span>
                        </>
                      )}
                    </div>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={cycleSort} data-testid="sort-accuracy-btn">
                      {confSort === 'desc' ? <ArrowDown className="w-3 h-3 mr-1" /> : confSort === 'asc' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowUpDown className="w-3 h-3 mr-1" />}
                      {confSort === 'desc' ? 'Accuracy: high → low' : confSort === 'asc' ? 'Accuracy: low → high' : 'Sort by accuracy'}
                    </Button>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                      <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search source or target value…"
                        className="h-8 w-64 pl-8 text-xs" data-testid="results-search-input" />
                    </div>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => downloadCsv('unmatched')} data-testid="download-unmatched-csv-btn"
                      title="Download only the unmatched records as CSV (no approval needed)">
                      <Download className="w-3 h-3 mr-1" /> Unmatched CSV
                    </Button>
                    <span className="text-xs text-muted-foreground ml-auto" data-testid="results-shown-count">
                      {resultsLoading ? 'Loading…' : resultsTotal > 0
                        ? `Rows ${resultsOffset + 1}–${Math.min(resultsOffset + PAGE_SIZE, resultsTotal)} of ${resultsTotal}`
                        : '0 rows'}
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto border rounded-md" data-testid="results-table">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted">
                        <tr>
                          {['Source value', 'Target value', 'Target PK', 'Method'].map((h) => (
                            <th key={h} className="text-left p-2 font-semibold">{h}</th>
                          ))}
                          <th className="text-left p-2 font-semibold cursor-pointer select-none hover:text-primary" onClick={cycleSort} data-testid="accuracy-column-header">
                            Accuracy {confSort === 'desc' ? '↓' : confSort === 'asc' ? '↑' : ''}
                          </th>
                          <th className="text-left p-2 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((r) => (
                          <tr key={r.id} className={`border-t ${editingResult?.id === r.id ? 'bg-amber-50' : ''}`}>
                            <td className="p-2 max-w-[220px] truncate">
                              {r.source_value}
                              {r.source_value2 != null && r.source_value2 !== '' && <span className="text-muted-foreground"> · {r.source_value2}</span>}
                            </td>
                            <td className="p-2 max-w-[220px] truncate">
                              {r.matched ? (
                                <>
                                  {r.target_value}
                                  {r.target_value2 != null && r.target_value2 !== '' && <span className="text-muted-foreground"> · {r.target_value2}</span>}
                                </>
                              ) : <span className="text-red-500">— unmatched —</span>}
                            </td>
                            <td className="p-2 font-mono">{r.target_pk ? `${r.target_pk.slice(0, 12)}…` : ''}</td>
                            <td className="p-2">
                              {r.method && <Badge variant="outline" style={{ color: METHOD_COLORS[r.method], borderColor: METHOD_COLORS[r.method] }}>{METHOD_LABELS[r.method]}</Badge>}
                            </td>
                            <td className="p-2">{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : ''}</td>
                            <td className="p-2 whitespace-nowrap">
                              <Button variant="ghost" size="sm" className="h-6 px-2" data-testid={`view-match-${r.id}`}
                                onClick={() => setViewResult(r)}>
                                <Eye className="w-3 h-3 mr-1" /> View
                              </Button>
                              {!job.approved && (
                                <>
                                  <Button variant="ghost" size="sm" className="h-6 px-2" data-testid={`edit-match-${r.id}`}
                                    onClick={() => { setEditingResult(r); setTargetQuery(''); setTargetOptions([]); }}>
                                    <Pencil className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                  {r.matched && (
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-red-600 hover:text-red-700" data-testid={`unmatch-${r.id}`}
                                      disabled={editBusy} onClick={() => editMatch(r.id, { action: 'unmatch' })}>
                                      <Unlink className="w-3 h-3 mr-1" /> Unlink
                                    </Button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!results.length && !resultsLoading && (
                          <tr><td colSpan={6} className="p-4 text-center text-muted-foreground" data-testid="no-results-row">No rows match the current filters.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {resultsTotal > PAGE_SIZE && (
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" disabled={resultsOffset === 0 || resultsLoading}
                        onClick={() => fetchResults(job.id, Math.max(0, resultsOffset - PAGE_SIZE))} data-testid="results-prev-btn">
                        ← Previous
                      </Button>
                      <span className="text-xs text-muted-foreground" data-testid="results-page-info">
                        Page {Math.floor(resultsOffset / PAGE_SIZE) + 1} of {Math.ceil(resultsTotal / PAGE_SIZE)}
                      </span>
                      <Button variant="outline" size="sm" disabled={resultsOffset + PAGE_SIZE >= resultsTotal || resultsLoading}
                        onClick={() => fetchResults(job.id, resultsOffset + PAGE_SIZE)} data-testid="results-next-btn">
                        Next →
                      </Button>
                    </div>
                  )}

                  {editingResult && !job.approved && (
                    <div className="border border-amber-300 bg-amber-50 rounded-md p-4 space-y-3" data-testid="match-edit-panel">
                      <div className="flex items-center justify-between">
                        <p className="text-sm">
                          Pick a new target for: <b>{editingResult.source_value?.slice(0, 80)}</b>
                        </p>
                        <Button variant="ghost" size="sm" onClick={() => setEditingResult(null)} data-testid="close-edit-panel-btn">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input placeholder="Search target values..." value={targetQuery} autoFocus
                        onChange={(e) => setTargetQuery(e.target.value)} data-testid="target-search-input" />
                      <div className="max-h-40 overflow-y-auto space-y-1" data-testid="target-options-list">
                        {targetOptions.map((t) => (
                          <button key={t.pk} disabled={editBusy} data-testid={`target-option-${t.pk}`}
                            onClick={() => editMatch(editingResult.id, { action: 'set', target_pk: t.pk })}
                            className="w-full text-left text-xs p-2 rounded border bg-white hover:bg-amber-100 transition-colors flex justify-between gap-3">
                            <span className="truncate">
                              {t.value || <i className="text-muted-foreground">(empty)</i>}
                              {t.value2 && <span className="text-muted-foreground"> · {t.value2}</span>}
                            </span>
                            <span className="font-mono text-muted-foreground shrink-0">{t.pk.slice(0, 8)}…</span>
                          </button>
                        ))}
                        {!targetOptions.length && <p className="text-xs text-muted-foreground p-1">No target rows found.</p>}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Approval + next steps */}
              {!job.approved ? (
                <>
                  {job.stats?.unmatched > 0 && (
                    <Card className="border-purple-300">
                      <CardContent className="py-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4 text-purple-600" /> Rerun unmatched with AI</p>
                            <p className="text-sm text-muted-foreground">
                              Retry only the {job.stats.unmatched} unmatched rows using the AI prompt and confidence configured above — no need to redo the whole job.
                            </p>
                          </div>
                          {!rerunOpen && (
                            <Button variant="outline" onClick={() => setRerunOpen(true)} data-testid="rerun-unmatched-btn"
                              className="text-purple-700 border-purple-300 hover:bg-purple-50">
                              <RefreshCw className="w-4 h-4 mr-2" /> Rerun unmatched
                            </Button>
                          )}
                        </div>
                        {rerunOpen && (
                          <div className="space-y-3 pt-2 border-t" data-testid="rerun-panel">
                            <div className="flex items-center gap-2">
                              <Switch id="rerun-all" checked={rerunAll} onCheckedChange={setRerunAll} data-testid="rerun-all-switch" />
                              <Label htmlFor="rerun-all" className="text-sm">
                                Process ALL {job.stats.unmatched} unmatched rows in one run
                                <span className="text-xs text-muted-foreground ml-2">(runs until done — AI cost applies to every row; you can cancel anytime)</span>
                              </Label>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              {!rerunAll && (
                                <>
                                  <Label className="text-sm">Rows to process:</Label>
                                  <Input type="number" min={1} max={1000} value={rerunLimit} className="w-24 h-9"
                                    onChange={(e) => setRerunLimit(Math.min(1000, Math.max(1, Number(e.target.value) || 1)))}
                                    data-testid="rerun-limit-input" />
                                  <span className="text-xs text-muted-foreground">of {job.stats.unmatched} unmatched (max 1000 per run)</span>
                                </>
                              )}
                              <Button disabled={rerunBusy} onClick={rerunUnmatched} data-testid="confirm-rerun-btn">
                                {rerunBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                                {rerunAll ? `Start full rerun (${job.stats.unmatched} rows)` : 'Start rerun'}
                              </Button>
                              <Button variant="ghost" onClick={() => setRerunOpen(false)} data-testid="cancel-rerun-btn">Cancel</Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  <Card className="border-primary/40">
                    <CardContent className="flex items-center justify-between py-5">
                      <div>
                        <p className="font-semibold">Review complete?</p>
                        <p className="text-sm text-muted-foreground">Approve the results to unlock CSV export and Azure SQL write-back.</p>
                      </div>
                      <Button onClick={approve} data-testid="approve-results-btn">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Approve Results
                      </Button>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Export</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">Download results as CSV — full set, matched only, or unmatched only.</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => downloadCsv('all')} data-testid="download-csv-btn">
                          <Download className="w-4 h-4 mr-2" /> All ({job.stats?.total_source})
                        </Button>
                        <Button variant="outline" onClick={() => downloadCsv('matched')} data-testid="download-matched-csv-btn">
                          <Download className="w-4 h-4 mr-2" /> Matched ({job.stats?.matched})
                        </Button>
                        <Button variant="outline" onClick={() => downloadCsv('unmatched')} data-testid="download-unmatched-csv-btn-2">
                          <Download className="w-4 h-4 mr-2" /> Unmatched ({job.stats?.unmatched})
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Write back to Azure SQL</CardTitle>
                      <CardDescription>Set a column in <b>{job.config.source_table}</b> to the matched target PK ({job.stats?.matched} rows).</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {job.writeback?.executed ? (
                        <p className="text-sm text-emerald-700" data-testid="writeback-done-msg">
                          ✓ Updated {job.writeback.rows_updated} rows in {job.writeback.table}.{job.writeback.column}
                        </p>
                      ) : (
                        <>
                          <Select value={wbColumn} onValueChange={(v) => { setWbColumn(v); setWbPreview(null); setWbTest(null); }}>
                            <SelectTrigger data-testid="writeback-column-select">
                              <SelectValue placeholder="Column to update" />
                            </SelectTrigger>
                            <SelectContent>
                              {wbEligibleCols.map((c) => (
                                <SelectItem key={c.name} value={c.name}>{c.name} ({c.data_type})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" disabled={!wbColumn || wbTestBusy} onClick={testWriteback} data-testid="writeback-test-btn">
                              {wbTestBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Test 1 record
                            </Button>
                            <Button variant="outline" size="sm" disabled={!wbColumn} onClick={downloadSqlScript} data-testid="writeback-script-btn">
                              <Download className="w-4 h-4 mr-1" /> Download SQL script
                            </Button>
                          </div>
                          {wbTest && (
                            <div className={`text-sm p-3 rounded-md border ${wbTest.success ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-red-300 bg-red-50 text-red-900'}`}
                              data-testid="writeback-test-result">
                              {wbTest.success ? (
                                <p>✓ {wbTest.message}</p>
                              ) : (
                                <>
                                  <p className="font-semibold">✗ Test failed — exact error:</p>
                                  <p className="font-mono text-xs mt-1 whitespace-pre-wrap break-words">{wbTest.error || wbTest.message}</p>
                                </>
                              )}
                              {wbTest.sample && (
                                <p className="text-xs mt-2 opacity-70">Tested row: PK <span className="font-mono">{wbTest.sample.source_pk}</span> → value <span className="font-mono">{wbTest.sample.new_value}</span></p>
                              )}
                            </div>
                          )}
                          {!wbPreview ? (
                            <Button variant="outline" disabled={!wbColumn || wbBusy} onClick={() => runWriteback(true)} data-testid="writeback-dryrun-btn">
                              {wbBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Preview update
                            </Button>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-sm" data-testid="writeback-preview-msg">
                                This will update <b>{wbPreview.rows_to_update}</b> rows in <b>{wbPreview.table}.{wbPreview.column}</b>. This cannot be undone.
                              </p>
                              <div className="flex gap-2">
                                <Button variant="destructive" disabled={wbBusy} onClick={() => runWriteback(false)} data-testid="writeback-confirm-btn">
                                  {wbBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm update
                                </Button>
                                <Button variant="ghost" onClick={() => setWbPreview(null)} data-testid="writeback-cancel-btn">Cancel</Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Match detail dialog */}
      <Dialog open={!!viewResult} onOpenChange={(o) => { if (!o) setViewResult(null); }}>
        <DialogContent className="max-w-2xl" data-testid="match-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Match Details</DialogTitle>
          </DialogHeader>
          {viewResult && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                {viewResult.matched ? (
                  <>
                    <Badge className="bg-emerald-600" data-testid="view-matched-badge">Matched</Badge>
                    {viewResult.method && (
                      <Badge variant="outline" style={{ color: METHOD_COLORS[viewResult.method], borderColor: METHOD_COLORS[viewResult.method] }}>
                        {METHOD_LABELS[viewResult.method]}
                      </Badge>
                    )}
                    {viewResult.confidence != null && <Badge variant="outline">Accuracy: {Math.round(viewResult.confidence * 100)}%</Badge>}
                  </>
                ) : (
                  <Badge variant="destructive" data-testid="view-unmatched-badge">Unmatched</Badge>
                )}
              </div>
              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Source — {job?.config.source_table}</p>
                <p><span className="text-muted-foreground">PK:</span> <span className="font-mono">{viewResult.source_pk}</span></p>
                <p className="whitespace-pre-wrap break-words"><span className="text-muted-foreground">{job?.config.source_column}:</span> {viewResult.source_value || <i className="text-muted-foreground">(empty)</i>}</p>
                {viewResult.source_value2 != null && (
                  <p className="whitespace-pre-wrap break-words"><span className="text-muted-foreground">{job?.config.source_column2}:</span> {viewResult.source_value2 || <i className="text-muted-foreground">(empty)</i>}</p>
                )}
              </div>
              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Target — {job?.config.target_table}</p>
                {viewResult.matched ? (
                  <>
                    <p><span className="text-muted-foreground">PK:</span> <span className="font-mono">{viewResult.target_pk}</span></p>
                    <p className="whitespace-pre-wrap break-words"><span className="text-muted-foreground">{job?.config.target_column}:</span> {viewResult.target_value || <i className="text-muted-foreground">(empty)</i>}</p>
                    {viewResult.target_value2 != null && (
                      <p className="whitespace-pre-wrap break-words"><span className="text-muted-foreground">{job?.config.target_column2}:</span> {viewResult.target_value2 || <i className="text-muted-foreground">(empty)</i>}</p>
                    )}
                  </>
                ) : (
                  <p className="text-red-500">No target matched for this row.</p>
                )}
              </div>
              {!job?.approved && job?.status === 'completed' && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" data-testid="view-dialog-edit-btn"
                    onClick={() => { setEditingResult(viewResult); setTargetQuery(''); setTargetOptions([]); setViewResult(null); }}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit match
                  </Button>
                  {viewResult.matched && (
                    <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" disabled={editBusy}
                      data-testid="view-dialog-unlink-btn"
                      onClick={async () => { await editMatch(viewResult.id, { action: 'unmatch' }); setViewResult(null); }}>
                      <Unlink className="w-4 h-4 mr-1" /> Unlink
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quality report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quality-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span className="flex items-center gap-2"><FileText className="w-5 h-5" /> Match Quality Report</span>
              <Button variant="outline" size="sm" onClick={printReport} data-testid="print-report-btn">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {reportData && (
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground">
                <b className="text-foreground">{reportData.config.source_table}.{reportData.config.source_column}</b> → <b className="text-foreground">{reportData.config.target_table}.{reportData.config.target_column}</b>
                <span className="mx-2">·</span>{reportData.created_at?.slice(0, 19).replace('T', ' ')}
                <span className="mx-2">·</span>{reportData.approved ? 'Approved' : 'Pending approval'}
                {reportData.writeback?.executed && <span className="mx-2">· Write-back: {reportData.writeback.rows_updated} rows</span>}
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-3xl font-bold text-primary" data-testid="report-match-rate">
                    {reportData.stats.total_source ? Math.round((reportData.stats.matched / reportData.stats.total_source) * 100) : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Match rate</p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-3xl font-bold text-emerald-600">{reportData.stats.matched}</p>
                  <p className="text-xs text-muted-foreground mt-1">Matched of {reportData.stats.total_source}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-3xl font-bold text-red-500">{reportData.stats.unmatched}</p>
                  <p className="text-xs text-muted-foreground mt-1">Unmatched</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-semibold mb-2">Method breakdown</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={Object.entries(reportData.stats.by_method).filter(([k]) => METHOD_LABELS[k]).map(([k, v]) => ({ name: METHOD_LABELS[k], count: v, fill: METHOD_COLORS[k] }))}>
                      <XAxis dataKey="name" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <p className="text-sm font-semibold mb-2">Confidence distribution (matched)</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={reportData.confidence_distribution}>
                      <XAxis dataKey="bucket" fontSize={10} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#4f46e5" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job history */}
      {jobs.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Previous Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="job-history-list">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => { fetchJob(j.id); setWbPreview(null); setWbColumn(''); setEditingResult(null); resetResultView(); }}
                  data-testid={`job-history-item-${j.id}`}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-md border text-sm hover:bg-muted transition-colors ${job?.id === j.id ? 'border-primary' : ''}`}>
                  <Badge variant={j.status === 'failed' ? 'destructive' : 'outline'}>{j.status}</Badge>
                  <span className="flex-1 truncate">
                    {j.config.source_table}.{j.config.source_column} → {j.config.target_table}.{j.config.target_column}
                  </span>
                  {j.stats && <span className="text-muted-foreground">{j.stats.matched}/{j.stats.total_source} matched</span>}
                  <span className="text-xs text-muted-foreground">{j.created_at?.slice(0, 19).replace('T', ' ')}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
